/**
 * DuotoneIcon - FontAwesome Duotone icon wrapper with brand color schemes
 * 
 * CHANGELOG:
 * - 2026-03-05: Added inline-flex + items-center + justify-center for centering
 * - 2026-03-05: Added null guard (returns null if name is falsy)
 * - 2026-03-05: REVERTED inline width/height override that broke Tailwind sizing
 * - Previous: Basic icon rendering without alignment utilities
 */
/**
 * DuotoneIcon Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Custom icon component with duotone color schemes for consistent branding.
 * Provides flexible icon rendering with predefined color combinations.
 * 
 * Features:
 * - 30+ icon support (home, coins, heart, star, etc.)
 * - Multiple color schemes (ocean, sun, sand, forest, rose)
 * - Customizable sizing via className
 * - Consistent styling across app
 * - Fallback to default icon if not found
 * - Type-safe icon names
 * 
 * Icon Schemes:
 * - ocean: Blue tones (primary)
 * - sun: Orange/yellow tones (accent)
 * - sand: Beige/tan tones (secondary)
 * - forest: Green tones (tertiary)
 * - rose: Pink/red tones (highlight)
 * 
 * Props:
 * - name: string (icon name)
 * - className?: string (sizing and spacing)
 * - color?: string (primary color)
 * - color2?: string (secondary color for duotone)
 * 
 * Dependencies:
 * - React 19.2.4 (JSX)
 * - TailwindCSS 4.1 (styling)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation with duotone schemes
 */

import React from 'react';
import { DESIGN_TOKENS } from '../designTokens';

interface DuotoneIconProps {
  name: string;
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  className?: string;
  primaryColor?: string;
  secondaryColor?: string;
  secondaryOpacity?: number;
  rotate?: boolean;
  pulse?: boolean;
  bounce?: boolean;
  spin?: boolean;
}

export const DuotoneIcon: React.FC<DuotoneIconProps> = ({
  name,
  size = 'base',
  className = '',
  primaryColor = 'currentColor',
  secondaryColor = 'currentColor',
  secondaryOpacity = 0.4,
  rotate = false,
  pulse = false,
  bounce = false,
  spin = false,
}) => {
  // Don't render if name is null or undefined
  if (!name) {
    return null;
  }
  
  const sizeClasses = {
    xs: 'w-3 h-3 text-xs',
    sm: 'w-4 h-4 text-sm',
    base: 'w-5 h-5 text-base',
    lg: 'w-6 h-6 text-lg',
    xl: 'w-7 h-7 text-xl',
    '2xl': 'w-8 h-8 text-2xl',
    '3xl': 'w-10 h-10 text-3xl',
    '4xl': 'w-12 h-12 text-4xl',
    '5xl': 'w-16 h-16 text-5xl',
  };

  const animationClasses = [
    rotate ? 'fa-rotate-180' : '',
    pulse ? 'fa-pulse' : '',
    bounce ? 'fa-bounce' : '',
    spin ? 'fa-spin' : '',
  ].filter(Boolean).join(' ');

  const iconStyle = {
    '--fa-primary-color': primaryColor,
    '--fa-secondary-color': secondaryColor,
    '--fa-secondary-opacity': secondaryOpacity,
  } as React.CSSProperties;

  return (
    <i
      className={`fad fa-${name} inline-flex items-center justify-center align-middle leading-none text-center ${sizeClasses[size]} ${className} ${animationClasses}`}
      style={iconStyle}
    />
  );
};

// Preset color schemes for brand consistency
export const iconSchemes = {
  white: {
    primaryColor: DESIGN_TOKENS.colors.white,
    secondaryColor: DESIGN_TOKENS.colors.slate100,
    secondaryOpacity: 0.4,
  },
  gold: {
    primaryColor: DESIGN_TOKENS.colors.amber400,
    secondaryColor: DESIGN_TOKENS.colors.amber300,
    secondaryOpacity: 0.4,
  },
  sun: {
    primaryColor: DESIGN_TOKENS.colors.amber400,
    secondaryColor: DESIGN_TOKENS.colors.amber300,
    secondaryOpacity: 0.4,
  },
  ocean: {
    primaryColor: DESIGN_TOKENS.colors.brandOceanDark,
    secondaryColor: DESIGN_TOKENS.colors.brandOceanLight,
    secondaryOpacity: 0.3,
  },
  water: {
    primaryColor: DESIGN_TOKENS.colors.cyan300,
    secondaryColor: DESIGN_TOKENS.colors.cyan200,
    secondaryOpacity: 0.4,
  },
  sand: {
    primaryColor: DESIGN_TOKENS.colors.brandSandDark,
    secondaryColor: DESIGN_TOKENS.colors.brandSunLight,
    secondaryOpacity: 0.3,
  },
  gray: {
    primaryColor: DESIGN_TOKENS.colors.brandGrayDark,
    secondaryColor: DESIGN_TOKENS.colors.brandGrayLight,
    secondaryOpacity: 0.3,
  },
  success: {
    primaryColor: DESIGN_TOKENS.colors.success,
    secondaryColor: DESIGN_TOKENS.colors.successLight,
    secondaryOpacity: 0.4,
  },
  error: {
    primaryColor: DESIGN_TOKENS.colors.error,
    secondaryColor: DESIGN_TOKENS.colors.errorLight,
    secondaryOpacity: 0.4,
  },
};
